import { BrandTileType } from '@/components/ContentfulBlock/BrandTiles'
import { FaqBlockProps } from '@/components/ContentfulBlock/FaqBlock'
import { CategoryType } from '@/components/ContentfulBlock/SpendCategories'
import { ContentfulBlock, DownloadAppBlockFields, HeroBlockFields, ImagesTextBlockFields } from '@/components/ContentfulBlock/types'

import { useContentTypeBySlug } from '@/query/contentful/useContentTypeBySlug'
import { media } from '@virgin-core/styles'
import { ContentfulPageSEO, convertLocaleToContentfulLocale, getSectionBackgroundColor } from '@vrw/data/src/utils/contentfulPages'
import React from 'react'
import { Page, PageLoader } from '../../components'
import { BrandTiles, DownloadAppBlock, FaqBlock, ImagesTextBlock, NewHeroBlock, SpendCategories } from '../../components/ContentfulBlock'
import { getLocale, showLocalisedContent } from '../../dataImplementation/viewingRegionWeb'
import { ErrorPage404 } from '../ErrorPage404'

type TopHeroSection = {
  topHero: ContentfulBlock<HeroBlockFields>
  textColour: ContentfulBlock<{ textColour: string }>
  backgroundColour: ContentfulBlock<{ colour: string }>
}

type SpendSection = {
  spendCategories: CategoryType[]
  spendHero: ContentfulBlock<HeroBlockFields>
  backgroundColour: ContentfulBlock<{ colour: string }>
  textColour: ContentfulBlock<{ textColour: string }>
}

type EarnSection = {
  brandTiles: BrandTileType[]
  earnHero: ContentfulBlock<HeroBlockFields>
  textColour: ContentfulBlock<{ textColour: string }>
}

type AppDownloadSection = {
  textColour: ContentfulBlock<{ textColour: string }>
  appDownloadHero: ContentfulBlock<DownloadAppBlockFields>
}

type HomePageFields = {
  seoMetadata: ContentfulPageSEO
  topHeroSection: {
    contentTypeId: string
    fields: TopHeroSection
  }
  spendSection?: {
    contentTypeId: string
    fields: SpendSection
  }
  earnSection?: {
    contentTypeId: string
    fields: EarnSection
  }
  howItWorksSection?: {
    contentTypeId: string
    fields: ImagesTextBlockFields
  }
  faqSection?: FaqBlockProps
  appDownloadSection?: {
    contentTypeId: string
    fields: AppDownloadSection
  }
}

export const HomePublic = () => {
  const locale = getLocale()

  const {
    data: content,
    isLoading,
    isError,
  } = useContentTypeBySlug<HomePageFields>({
    contentType: 'homePublic',
    slug: 'homePublic',
    viewingRegion: convertLocaleToContentfulLocale(locale),
  })

  const publicSEOFields = content?.seoMetadata.fields
  const { topHeroSection, spendSection, earnSection, howItWorksSection, faqSection, appDownloadSection } = content || {}
  const earnTiles = earnSection?.fields?.brandTiles ?? []
  const spendCategories = spendSection?.fields?.spendCategories ?? []

  if (isError) {
    return <ErrorPage404 />
  }

  return (
    <>
      <style jsx>{`
        .top-hero-section {
          background-color: ${topHeroSection && getSectionBackgroundColor(topHeroSection)};
        }
        .spend-section {
          background-color: ${spendSection && getSectionBackgroundColor(spendSection)};
        }
        .earn-section {
          background-color: ${earnSection && getSectionBackgroundColor(earnSection)};
        }
        .how-it-works-section {
          background-color: ${howItWorksSection && getSectionBackgroundColor(howItWorksSection)};
        }
        .faq-section {
          background-color: ${faqSection && getSectionBackgroundColor(faqSection as any)};
        }
        .download-hero-section {
          background-color: ${appDownloadSection && getSectionBackgroundColor(appDownloadSection)};
        }
        .earn-section,
        .spend-section,
        .how-it-works-section,
        .faq-section {
          padding: 40px 0;
        }

        @media ${media.tabletAndHigher} {
          .earn-section {
            padding: 40px 0 28px;
          }
          .spend-section {
            padding: 40px 0 28px;
          }
        }

        @media ${media.tabletLargeAndHigher} {
          .top-hero-section :global(.text-panel) {
            padding-top: 40px;
          }
        }

        @media ${media.hiResDesktop} {
          .top-hero-section :global(.text-panel) {
            padding-top: 100px;
          }
          .earn-section {
            padding: 96px 0 85px;
          }
          .spend-section {
            padding: 96px 0 72px;
          }

          .how-it-works-section,
          .faq-section {
            padding: 96px 0;
          }
        }
      `}</style>
      <Page
        virtualPagePath="/virgin-red/*"
        testId="page-home"
        title={publicSEOFields?.title}
        metaDescription={publicSEOFields?.description}>
        {isLoading ? (
          <PageLoader />
        ) : (
          <>
            {topHeroSection && (
              <div data-testid="top-hero-section" className="top-hero-section">
                <NewHeroBlock
                  sectionName="top_hero_section"
                  fields={topHeroSection.fields.topHero?.fields}
                  textColour={topHeroSection.fields.textColour?.fields.textColour}
                  topHero
                />
              </div>
            )}
            {showLocalisedContent && (
              <>
                {earnSection && (
                  <div data-testid="earn-hero-section" className="earn-section">
                    <NewHeroBlock
                      sectionName="earn_section"
                      fields={earnSection.fields.earnHero?.fields}
                      textColour={earnSection.fields.textColour?.fields.textColour}>
                      {earnTiles.length > 0 && <BrandTiles tiles={earnTiles} />}
                    </NewHeroBlock>
                  </div>
                )}
                {spendSection && (
                  <div data-testid="spend-hero-section" className="spend-section">
                    <NewHeroBlock
                      sectionName="spend_section"
                      fields={spendSection.fields.spendHero?.fields}
                      textColour={spendSection.fields.textColour?.fields.textColour}>
                      {spendCategories.length > 0 && <SpendCategories sectionName="spend_section" categories={spendCategories} />}
                    </NewHeroBlock>
                  </div>
                )}
                {howItWorksSection && (
                  <div data-testid="how-it-works-section" className="how-it-works-section">
                    <ImagesTextBlock sectionName="how_it_works_section" fields={howItWorksSection.fields} />
                  </div>
                )}
                {faqSection && (
                  <div data-testid="faq-section" className="faq-section">
                    <FaqBlock sectionName="faq_section" fields={faqSection.fields} />
                  </div>
                )}
                {appDownloadSection && (
                  <div data-testid="download-hero-section" className="download-hero-section">
                    <DownloadAppBlock
                      sectionName="download_section"
                      fields={appDownloadSection.fields.appDownloadHero?.fields}
                      textColour={appDownloadSection.fields.textColour?.fields.textColour}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Page>
    </>
  )
}
